/**
 * Processing Screen Component
 *
 * Displays loading state while the template is being analyzed.
 */

import React from 'react';
import { AutoAwesome as AIIcon } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  LinearProgress,
  Paper,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

interface ProcessingScreenProps {
  /** Progress message from the backend */
  progressMessage: string | null;
  /** Whether polling is active */
  isPolling?: boolean;
  /** Document title (for PDFs, available early) */
  documentTitle?: string | null;
  /** Document summary (for PDFs, available early) */
  documentSummary?: string | null;
}

/**
 * Processing Screen - Shows loading state during template analysis
 */
export const ProcessingScreen: React.FC<ProcessingScreenProps> = ({
  progressMessage,
  isPolling = true,
  documentTitle,
  documentSummary,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        p: 4,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 500,
          p: 4,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          textAlign: 'center',
        }}
      >
        {/* Icon with animation */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
          }}
        >
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress
              size={64}
              thickness={2}
              sx={{
                color: theme.palette.primary.main,
              }}
            />
            <AIIcon
              sx={{
                position: 'absolute',
                fontSize: 28,
                color: theme.palette.primary.main,
              }}
            />
          </Box>
        </Box>

        {/* Document Title (if available from PDF analysis) */}
        {documentTitle && (
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            {documentTitle}
          </Typography>
        )}

        {/* Title */}
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {documentTitle ? 'Generating Template Structure' : 'Analyzing Your Template'}
        </Typography>

        {/* Document Summary (if available) */}
        {documentSummary && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, fontStyle: 'italic', textAlign: 'left' }}
          >
            {documentSummary}
          </Typography>
        )}

        {/* Progress message */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {progressMessage || 'Processing document and extracting template structure...'}
        </Typography>

        {/* Indeterminate progress bar */}
        {isPolling && (
          <LinearProgress
            sx={{
              borderRadius: 1,
              height: 6,
              bgcolor: theme.palette.grey[200],
              '& .MuiLinearProgress-bar': {
                borderRadius: 1,
              },
            }}
          />
        )}

        {/* Help text */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 3, display: 'block' }}
        >
          This usually takes less than a minute
        </Typography>
      </Paper>
    </Box>
  );
};

